import { useEffect, useState } from 'react';
import { Page } from '../components/Page.js';

type Preview = { ready: boolean; status: string | null; head_seq: number | null; court_grade: boolean };

export function Reports() {
  const [p, setP] = useState<Preview | null>(null);

  useEffect(() => {
    fetch('/api/export/ppu/preview').then((r) => r.json()).then(setP).catch(() => {});
  }, []);

  return (
    <Page eyebrow="Evidence & access" title="Register & reports">
      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>PPU proof packet</h3>
        <p className="muted" style={{ fontSize: 13 }}>
          A self-verifying zip for the Public Protection Unit, a solicitor, or a court. It contains the
          full ledger up to a Bitcoin-anchored head, the OpenTimestamps proof, the dated notifications and
          reconciliations, and a one-command verifier. Anyone can confirm on a clean machine — with only
          Node and the public <code>ots</code> tool — that these records existed by the anchor’s Bitcoin
          time and were not back-dated. <b>No passphrase or financial secrets are included.</b>
        </p>

        {p && !p.ready && (
          <div style={{ color: 'var(--warn)', fontSize: 13 }}>
            No anchor yet — open <b>Verify</b> and click “Anchor now”, then come back.
          </div>
        )}
        {p && p.ready && (
          <>
            <div className="muted" style={{ fontSize: 12.5, marginBottom: 10 }}>
              Latest anchor: head seq {p.head_seq} · {p.status}
              {p.court_grade
                ? ' · court-grade (Bitcoin-confirmed)'
                : ' · pending Bitcoin confirmation (fine for day-to-day; re-export once confirmed for a court-grade packet)'}
            </div>
            <a className="btn btn-primary" href="/api/export/ppu" download>Download proof packet (.zip)</a>
          </>
        )}
      </section>

      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Deadlines export</h3>
        <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          Your notification register as a spreadsheet, or a calendar feed so every deadline (with a 7-day
          reminder) lands in your own calendar. These contain duties and dates only — no financial secrets.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <a className="btn btn-ghost" href="/api/export/reminders.csv" download>Download register (.csv)</a>
          <a className="btn btn-ghost" href="/api/export/reminders.ics" download>Calendar feed (.ics)</a>
        </div>
      </section>

      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Former devices report</h3>
        <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          A printable record of every device that has left your possession (disposed, sold, lost, stolen
          or returned), with its full notes and every recorded lifecycle event — so you can explain when
          and why a device went, should you ever be asked. These are <b>not</b> on your notification pack,
          which lists only what you currently hold.
        </p>
        <a className="btn btn-ghost" href="/api/export/former-devices.pdf" download>Download former devices (.pdf)</a>
      </section>

      <section className="card card-pad">
        <h3 style={{ marginTop: 0, fontSize: 14 }}>What’s inside</h3>
        <ul className="muted" style={{ fontSize: 13, lineHeight: 1.7, margin: 0, paddingLeft: 18 }}>
          <li><code>VERIFY.md</code> — step-by-step instructions, self-contained.</li>
          <li><code>verify.mjs</code> — zero-dependency verifier; replays the hash chain and checks it ties to the anchor.</li>
          <li><code>ledger_segment.json</code> — every event (canonical bytes + nonce + hashes) up to the anchored head.</li>
          <li><code>chainhead.txt</code> + <code>chainhead.txt.ots</code> — the anchored hash and its Bitcoin timestamp proof.</li>
          <li><code>notifications.json</code> / <code>reconciliations.json</code> — what was reported to the police, and when.</li>
          <li><code>manifest.json</code> — chain id, spec, anchor status, and a SHA-256 of every file.</li>
        </ul>
      </section>
    </Page>
  );
}
